import React, { useState, useEffect, useCallback } from 'react'
import { Flex, Box } from 'reflexbox'
import { toast } from 'react-toastify'
import { useParams, useHistory } from 'react-router-dom'

import DataListingTable from 'components/DataListingTable'
import Loader from 'components/Loader'
import { SearchInput } from 'components/Form'
import { Title } from 'components/Typography'
import Button from 'components/Button'

import { getBeneficiaries } from 'services/beneficiaries'
import * as partnersService from 'services/partners'
import { useDebounce } from 'hooks/debounce'

import { formatBeneficiaryDocument } from 'helpers/formatters'

const TableColumns = [
  {
    Header: 'Nome',
    accessor: 'name',
  },
  {
    Header: 'CPF/CNPJ',
    accessor: 'document',
  },
  {
    Header: 'Email',
    accessor: 'email',
  },
  {
    Header: 'Dia de cobrança',
    accessor: 'activation_day',
    id: 'activation_date__day',
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
  {
    Header: 'Pacotes',
    accessor: 'packages',
    id: 'groups_count',
  },
  {
    Header: 'Parceiro responsável',
    accessor: 'partner',
    id: 'partner__company_name',
  },
]

const formatBeneficiariesData = data => {
  return data.map(beneficiary => ({
    ...beneficiary,
    document: formatBeneficiaryDocument(beneficiary.document),
    status: translateStatus(beneficiary.status),
    packages: beneficiary.groups.map(group => group.packages_names).join(','),
    partner: beneficiary.partner.company_name,
  }))
}

const translateStatus = status => {
  switch (status) {
    case 'active':
      return 'Ativo'
    case 'deactive':
      return 'Inativo'
    case 'waiting':
      return 'Aguardando ativação'
  }
}

const List = () => {
  const [isTableLoading, setIsTableLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [tableData, setTableData] = useState([])
  const [sortBy, setSortBy] = useState(null)
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
  })
  const [partnerCompanyName, setPartnerCompanyName] = useState(null)

  const handleTableDataFetch = ({ sortBy, currentPage, pageSize }) => {
    setSortBy(sortBy)
    setPagination({ ...pagination, currentPage, pageSize })
  }

  const { partnerID } = useParams()
  const history = useHistory()

  const searchValue = useDebounce(search, 500)

  const getBeneficiariesData = useCallback(async () => {
    try {
      setIsTableLoading(true)
      const res = await getBeneficiaries({ pagination, sortBy, search: searchValue, partner_id: partnerID })

      setTableData(formatBeneficiariesData(res.beneficiaries))
      setPagination(res.pagination)
    } catch (e) {
      console.log(e)
      toast.error('Erro ao carregar dados de beneficiários.')
    } finally {
      setIsTableLoading(false)
    }
  }, [pagination, searchValue, sortBy])

  useEffect(() => {
    setIsLoading(false)
    if (sortBy) {
      getBeneficiariesData()
    }
  }, [sortBy, pagination.currentPage, pagination.pageSize])

  useEffect(() => {
    if (pagination.currentPage === 1 && searchValue) {
      getBeneficiariesData()
    }
    setPagination({ ...pagination, currentPage: 1 })
  }, [searchValue])

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const { data } = await partnersService.getPartner(partnerID)
        setPartnerCompanyName(data.company_name)
      } catch (e) {
        console.log(e)
        toast.error('Não foi possível carregar a razão social do parceiro')
      }
    }

    if (partnerID) {
      fetchPartner()
    } else {
      setPartnerCompanyName(null)
    }
  }, [partnerID])

  if (isLoading) {
    return (
      <Flex height='80vh' alignItems='center' justifyContent='center'>
        <Loader />
      </Flex>
    )
  }

  return (
    <Flex width={1} flexDirection='column'>
      <Box mb={32}>
        <Title as='h3'>{partnerCompanyName && `${partnerCompanyName} | `} Lista de Beneficiários</Title>
      </Box>

      <Flex justifyContent='space-between'>
        <Flex alignItems='center' justifyContent='flex-start' mb={24}>
          <SearchInput
            name='search'
            label='Buscar'
            placeholder='Buscar'
            onChange={e => setSearch(e.target.value)}
            mr={24}
          />
          {isLoading && <Loader />}
        </Flex>

        {partnerID && (
          <Button onClick={() => history.push(`/staff/partners/list/${partnerID}`)} outlined>
            Voltar
          </Button>
        )}
      </Flex>
      <DataListingTable
        columns={TableColumns}
        data={tableData}
        pagination={pagination}
        fetchData={handleTableDataFetch}
        loading={isTableLoading}
      />
    </Flex>
  )
}

export default List
